
import { computed, ComputedRef, onMounted, reactive, ref, watch } from 'vue';

import { Box, Shelf, BoxDimensions, Material, isValidShelf, Machine } from '../logic/calculator';
import { boxCoordinates } from '../logic/boxCoordinates';
import {
	createTestBox,
	createBox,
	createBoxToArray,
	calculatePossibleBasicBoxDimensions,
	calculatePossibleUserBoxDimensions,
	updatBoxDimensions,
} from '../logic/box';
import { calculateGrid, resetGrid } from '../logic/boxGrid';
import { downloadBoxSVG, downloadBoxesSVG, getSVG } from '../logic/svg';

import GridStack from 'gridstack/dist/gridstack-h5.js';
import 'gridstack/dist/gridstack.css';
import 'gridstack/dist/gridstack-extra.css';
import { GridStackOptions } from 'gridstack';
import { unitType } from 'makerjs';
import { useStore } from 'vuex';

function createTestBoxes(shelf: Shelf, possibleUserBoxDimensions: BoxDimensions): Box[] {
	return reactive<Array<Box>>([
		reactive(
			createTestBox(
				shelf,
				possibleUserBoxDimensions,
				0,
				possibleUserBoxDimensions.heights.length,
				possibleUserBoxDimensions.heights[0],
				possibleUserBoxDimensions.widths[1],
			),
		),
		reactive(
			createTestBox(
				shelf,
				possibleUserBoxDimensions,
				2,
				possibleUserBoxDimensions.heights.length,
				possibleUserBoxDimensions.heights[0],
				possibleUserBoxDimensions.widths[1],
			),
		),
		reactive(
			createTestBox(
				shelf,
				possibleUserBoxDimensions,
				1,
				possibleUserBoxDimensions.heights.length,
				possibleUserBoxDimensions.heights[1],
				possibleUserBoxDimensions.widths[1],
			),
		),
		reactive(
			createTestBox(
				shelf,
				possibleUserBoxDimensions,
				4,
				possibleUserBoxDimensions.heights.length,
				possibleUserBoxDimensions.heights[2],
				possibleUserBoxDimensions.widths[0],
			),
		),
		reactive(
			createTestBox(
				shelf,
				possibleUserBoxDimensions,
				possibleUserBoxDimensions.widths.length,
				possibleUserBoxDimensions.heights.length,
				possibleUserBoxDimensions.heights[0],
				possibleUserBoxDimensions.widths[3],
			),
		),
	]);
}

const testShelf = reactive<Shelf>({
	height: 2000,
	width: 3000,
	depth: 400,
});

function gridOptions(shelf: Shelf, basicBox: Box): GridStackOptions {
	const shelfHeight = shelf.height ? shelf.height : 0;
	const shelfWidth = shelf.width ? shelf.width : 0;

	return {
		column: shelfWidth / basicBox.width,
		row: shelfHeight / basicBox.height,
		disableOneColumnMode: true,
		float: true,
		cellHeight: `50px`,
	};
}
export default {
	name: 'BoxSystem',

	props: {
		machine: {
			type: Object as () => Machine,
			required: true,
		},
		material: {
			type: Object as () => Material,
			required: true,
		},
		unit: {
            type: String,
            default: 'Millimeter',
        },
	},

	setup(props) {

		const store = useStore();

		const activeBox = computed(() => store.state.activeBox);
		const basicBoxSteps = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
		const userBoxSteps = [1, 2, 3, 4, 5];

		const materialRef = ref<Material>(props.material);

		const machine = reactive<Machine>(props.machine);

		let mounted = false;

		const shelf = reactive<Shelf>({});

		let userBoxes = reactive<Box[]>([]);

		const gridItemButtons = computed(() => document.getElementsByClassName('gridItem__button'));

		const possibleBasicBoxDimensions = computed(
			(): BoxDimensions => calculatePossibleBasicBoxDimensions(shelf, machine, basicBoxSteps),
		);

		const basicBox = reactive<Box>(createBox(shelf, possibleBasicBoxDimensions.value));

		const possibleUserBoxDimensions: ComputedRef<BoxDimensions> = computed(
			(): BoxDimensions => calculatePossibleUserBoxDimensions(shelf, machine.processingArea, basicBox, userBoxSteps),
		);

		// const userBoxes = reactive<Box[]>(createTestBoxes(shelf, possibleUserBoxDimensions));

		const info = ref('');

		const invalidBoxes = ref<Box[]>([]);

		let grid: GridStack;

		onMounted(() => {
			mounted = true;

			grid = GridStack.init(gridOptions(shelf, basicBox));

			calculateGrid(grid, shelf, basicBox, userBoxes);
		});

		watch(shelf, () => {
			if (isValidShelf(shelf, machine)) {
				basicBox.possibleBoxDimensions = possibleBasicBoxDimensions.value;
				basicBox.height = basicBox.possibleBoxDimensions.heights[0];
				basicBox.width = basicBox.possibleBoxDimensions.widths[0];
				basicBox.depth = shelf.depth ? shelf.depth : 0;
			}
		});

		watch([shelf, basicBox, userBoxes], () => {
			if (mounted) {
				// TODO change rows & columns of grid based on basic box
				resetGrid('grid');
				calculateGrid(gridOptions(shelf, basicBox), shelf, basicBox, userBoxes);
			}
		});

		watch(possibleUserBoxDimensions, (newBoxDimesnions) => {
			if (mounted) {
				userBoxes.forEach((box) => updatBoxDimensions(box, newBoxDimesnions));
			}
		});

		watch(userBoxes, () => {
			const boxes: Box[] = [];
			userBoxes.forEach((box: Box) => {
				if (!box.validDimensions) boxes.push(box);
			});
			invalidBoxes.value = boxes;

			const gridButtons = (document.getElementsByClassName('griditem__button'));
		});

		function fillTestData(): void {
			shelf.height = testShelf.height;
			shelf.width = testShelf.width;
			shelf.depth = testShelf.depth;
		}

		function fillTestBoxes(): void {
			if(possibleUserBoxDimensions.value.heights.length > 0) {
				const testBoxes = reactive<Box[]>(createTestBoxes(shelf, possibleUserBoxDimensions.value));
				testBoxes.forEach((box) => userBoxes.push(box));
			}
		}

		function deleteBox() {
			const box = activeBox.value;
			userBoxes = userBoxes.filter((b) => b.id !== box.id);
			console.log(userBoxes);
			resetGrid('grid');
			calculateGrid(gridOptions(shelf, basicBox), shelf, basicBox, userBoxes);
			store.commit('setActiveBox', undefined);
		}

		return {
			shelf,
			basicBox,
			possibleBasicBoxDimensions,
			userBoxes,
			possibleUserBoxDimensions,
			downloadBoxSVG,
			downloadBoxesSVG,
			boxCoordinates,
			materialRef,
			invalidBoxes,
			info,
			unitType,
			activeBox,
			gridItemButtons,

			// Functions
			createBox,
			getSVG,
			createBoxToArray,
			fillTestData,
			fillTestBoxes,
			deleteBox,
		};
	},
};
